<template>
  <div class="json">
    <section
      class="json-flyout-toggle"
      @click="toggleFlyout"
    >
      <div class="json-flyout-toggle--left">
        <fn-icon
          icon="arrow-down"
          :class="{
            'icon--open': isToggled,
          }"
        />
        <b v-text="name" />
      </div>

      <div class="json-flyout-toggle--right">
        <fn-checkbox
          v-model:value="showLineNumbers"
          name="showLineNumbers"
          label="Show line numbers"
        />
      </div>
    </section>

    <transition mode="in-out">
      <div
        v-show="isToggled"
        class="grow"
        :style="{
          height: `${heightWithSpacing}px`,
        }"
      >
        <vue-json-pretty
          ref="jsonPretty"
          :show-line-number="showLineNumbers"
          :data="jsonData"
          :style="{
            height: `${heightWithSpacing}px`,
          }"
          :height="height"
          virtual
        />
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import VueJsonPretty from 'vue-json-pretty';
import type { JSONDataType } from 'vue-json-pretty/types/utils';
import 'vue-json-pretty/lib/styles.css';
import type { MaybeRefOrGetter } from 'vue';

const props = withDefaults(
  defineProps<{
    name: string;
    data: MaybeRefOrGetter<JSONDataType>;
    maxHeight?: number;
  }>(),
  {
    maxHeight: 400,
  },
);

const lineCountRegex = /\r\n|\r|\n/;
const lineHeight = 20;
const showLineNumbers = ref(false);

const { $log } = useNuxtApp();
const jsonPrettyRef = useTemplateRef<ComponentPublicInstance>('jsonPretty');
const jsonData = computed(() => toValue(props.data));
const jsonLineCount = computed(() => {
  try {
    return JSON.stringify(unref(jsonData), null, 2)?.split?.(lineCountRegex)?.length * lineHeight;
  } catch (e) {
    $log.error(e);
  }
});

const { height, updateHeight } = useElementHeight(jsonPrettyRef, {
  maxHeight: props.maxHeight,
  transform: () => (unref(isToggled) ? unref(jsonLineCount) : 0),
});

const heightWithSpacing = computed(() => unref(height) + 16 * 1.5);

const [isToggled, toggle] = useToggle();

const toggleFlyout = () => {
  toggle();
  nextTick().then(updateHeight);
};
</script>

<style lang="scss" scoped>
.json {
  display: flex;
  flex-direction: column;
  background: $color-grey-neutral-100;
  border-radius: map-get($border-radiuses, 'l');
  cursor: pointer;
  overflow: hidden;

  &-flyout-toggle {
    display: flex;
    justify-content: space-between;
    padding: $pad;

    &--left,
    &--right {
      display: flex;
      align-items: center;
      gap: $pad * 0.5;
    }

    .icon {
      display: inline-block;
      transition: transform var(--transition-duration) ease-in-out;

      &--open {
        transform: rotate(-180deg);
      }
    }
  }

  .grow {
    position: relative;

    // Border as fake padding between content and scrollbars
    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: calc(100% - #{$pad * 0.5});
      height: calc(100% - #{$pad * 0.5});
      border: solid $pad $color-grey-neutral-100;
      border-top-width: 0;
      border-left-width: 0;
      pointer-events: none;
    }
  }

  .vjs-tree {
    // Overriding inline style of element we can't access otherwise
    padding: 0 $pad $pad $pad !important;
    transition: height var(--transition-duration) ease-in-out;

    scrollbar-color: grey transparent;
    scrollbar-width: thin;

    //&::-webkit-scrollbar {
    //  display: none;
    //}
  }
}

.grow {
  transition: height var(--transition-duration) ease-in-out;
}
</style>
